import './HorizontalPager.scss';
import React, { RefObject, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

const STORAGE_KEY = 'winnerSelection.pagerStep';
const STEP_OPTIONS = [1, 3, 5] as const;
type StepOption = (typeof STEP_OPTIONS)[number];
const DEFAULT_STEP: StepOption = 3;

const isStepOption = (n: number): n is StepOption => (STEP_OPTIONS as readonly number[]).includes(n);

const readStep = (): StepOption => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT_STEP;
        const n = Number(raw);
        return isStepOption(n) ? n : DEFAULT_STEP;
    } catch {
        return DEFAULT_STEP;
    }
};

interface IProps {
    /** Количество прокручиваемых столбцов в текущем виде (предложения или критерии). */
    columnsCount: number;
    /** Ширина одного прокручиваемого столбца (px) — для расчёта видимого диапазона. */
    columnWidth: number;
    /** Ширина sticky-первого столбца (px) — он не считается прокручиваемым. */
    firstColWidth: number;
    /** Scroll-контейнер таблицы — pager сам слушает его scroll для подписи диапазона. */
    scrollRef: RefObject<HTMLElement | null>;
    canScrollLeft: boolean;
    canScrollRight: boolean;
    /** Прокрутить на N колонок в направлении dir. N управляется самим pager'ом. */
    onScroll: (dir: 1 | -1, columns: number) => void;
}

/**
 * Плавающий горизонтальный pager в нижнем правом углу таблицы.
 *
 * Состав: сегмент выбора шага (1/3/5 столбцов, выезжает слева на hover) + кнопки
 * прокрутки влево/вправо + подпись «видимый диапазон из всего».
 * Виден только когда есть горизонтальный скролл. Шаг сохраняется в localStorage.
 *
 * Видимый диапазон столбцов pager считает сам, подписываясь на scroll контейнера —
 * это изолирует частые scroll-обновления в pager'е, не ре-рендеря всю матрицу.
 */
export const HorizontalPager: React.FC<IProps> = ({
    columnsCount,
    columnWidth,
    firstColWidth,
    scrollRef,
    canScrollLeft,
    canScrollRight,
    onScroll,
}) => {
    const [step, setStepState] = useState<StepOption>(readStep);

    const setStep = useCallback((n: StepOption) => {
        setStepState(n);
        try {
            localStorage.setItem(STORAGE_KEY, String(n));
        } catch {
            /* localStorage недоступен (приватный режим Safari и т.п.) */
        }
    }, []);

    /* Видимый диапазон прокручиваемых столбцов [first..last] — для подписи в центре. */
    const [range, setRange] = useState<{ first: number; last: number }>({
        first: 1,
        last: columnsCount,
    });
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const recalc = () => {
            /* Видимая ширина прокручиваемой зоны = вьюпорт минус sticky-колонка. */
            const visible = Math.max(0, el.clientWidth - firstColWidth);
            const first = Math.min(columnsCount, Math.floor(el.scrollLeft / columnWidth) + 1);
            const last = Math.min(columnsCount, Math.max(first, Math.ceil((el.scrollLeft + visible) / columnWidth)));
            setRange({ first, last });
        };
        recalc();
        el.addEventListener('scroll', recalc, { passive: true });
        const ro = new ResizeObserver(recalc);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', recalc);
            ro.disconnect();
        };
    }, [scrollRef, columnWidth, firstColWidth, columnsCount]);

    /* Pager нужен только когда есть горизонтальный скролл (контент шире вьюпорта). */
    if (!canScrollLeft && !canScrollRight) return null;

    /* Русский плюрал для «столбец» в текущей раскладке шагов 1/3/5:
       1 → «столбец», 2-4 → «столбца», 5+ → «столбцов». */
    const columnsWord = (n: number): string => (n === 1 ? 'столбец' : n >= 2 && n <= 4 ? 'столбца' : 'столбцов');
    const stepLabel = `${step} ${columnsWord(step)}`;
    const rangeText = range.last > range.first ? `${range.first}–${range.last}` : `${range.first}`;

    return (
        <div className="ws-h-pager" role="group" aria-label="Горизонтальная навигация по столбцам">
            <div className="ws-h-pager__steps" role="radiogroup" aria-label="Шаг прокрутки">
                {STEP_OPTIONS.map((n) => (
                    <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={step === n}
                        className={clsx('ws-h-pager__step', { 'is-active': step === n })}
                        onClick={() => setStep(n)}
                        title={`Прокрутка на ${n} ${columnsWord(n)}`}
                    >
                        {n}
                    </button>
                ))}
            </div>
            <button
                type="button"
                className="ws-h-pager__btn"
                onClick={() => onScroll(-1, step)}
                disabled={!canScrollLeft}
                aria-label="Прокрутить влево"
                title={`Прокрутить на ${stepLabel} влево`}
            >
                <MdChevronLeft size="20px" />
            </button>
            <span className="ws-h-pager__count" aria-live="polite">
                <b className="ws-h-pager__count-range">{rangeText}</b>
                <span className="ws-h-pager__count-total">из {columnsCount}</span>
            </span>
            <button
                type="button"
                className="ws-h-pager__btn"
                onClick={() => onScroll(1, step)}
                disabled={!canScrollRight}
                aria-label="Прокрутить вправо"
                title={`Прокрутить на ${stepLabel} вправо`}
            >
                <MdChevronRight size="20px" />
            </button>
        </div>
    );
};
